import { useState } from 'react';
import {
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  MapPinIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/solid';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    preferredContact: 'email',
  });

  const contactChannels = [
    {
      method: 'Email',
      value: 'support@willowvalecu.com',
      icon: EnvelopeIcon,
      description: 'Send us your inquiries anytime.',
      availability: 'Response within 24 hours',
    },
    {
      method: 'Phone',
      value: '(808) 860-2349',
      icon: PhoneIcon,
      description: 'Speak directly with our team.',
      availability: '24/7',
    },
    {
      method: 'Live chat',
      value: 'Online',
      icon: ChatBubbleLeftRightIcon,
      description: 'Instant support through our website.',
      availability: 'Mon–Fri, 9AM–6PM ET',
    },
  ];

  const supportPoints = [
    { icon: ClockIcon, title: '24/7 support', description: 'Our team is here when you need us.' },
    { icon: UserGroupIcon, title: 'Dedicated team', description: 'Personal bankers ready to help.' },
    { icon: ShieldCheckIcon, title: 'Secure', description: 'Your information is always protected.' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      <section className="relative pt-24 pb-16 lg:pt-28 lg:pb-20 bg-[#0a1628] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-primary-400 text-sm font-semibold tracking-widest uppercase mb-4">Contact</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            We’re here to help
            <br />
            <span className="text-primary-300">every step of the way</span>
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Get in touch with our team for assistance with your accounts, loans, or any question about banking with Willowvale.
          </p>
        </div>
      </section>

      {/* Contact channels */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {contactChannels.map((channel) => (
              <div
                key={channel.method}
                className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="p-3 bg-primary-100 rounded-xl w-fit mb-5">
                  <channel.icon className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">{channel.method}</h3>
                <p className="text-slate-600 text-sm mb-3">{channel.description}</p>
                <p className="font-semibold text-primary-600">{channel.value}</p>
                <p className="text-slate-500 text-sm mt-1">{channel.availability}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & map */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Visit us</h2>
            <p className="text-slate-600 text-lg">Come see us at our branch in Honolulu.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-stretch">
            <div className="lg:col-span-2 p-8 bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-center">
              <p className="text-slate-600 flex items-start gap-3 mb-4">
                <MapPinIcon className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
                600 Kapiolani Blvd, Honolulu, HI 96813, USA
              </p>
              <p className="text-slate-600 flex items-center gap-3 mb-2">
                <PhoneIcon className="h-5 w-5 text-primary-600 shrink-0" />
                <a href="tel:+18088602349" className="hover:text-primary-600">(808) 860-2349</a>
              </p>
              <p className="text-slate-500 text-sm mb-6">Reg No: 0827788</p>
              <a
                href="https://www.google.com/maps/search/?api=1&query=600+Kapiolani+Blvd,+Honolulu,+HI+96813"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700"
              >
                Get directions
                <ArrowRightIcon className="ml-1 h-4 w-4" />
              </a>
            </div>
            <div className="lg:col-span-3 rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 min-h-[280px] sm:min-h-[320px]">
              <iframe
                title="Willowvale Credit Union - 600 Kapiolani Blvd, Honolulu, HI 96813"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-157.864%2C21.294%2C-157.852%2C21.304&layer=mapnik&marker=21.299%2C-157.858"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '280px' }}
                className="w-full h-full min-h-[280px] sm:min-h-[320px]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Send us a message</h2>
            <p className="text-slate-600 text-lg">We’ll get back to you as soon as we can.</p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-slate-50 rounded-2xl p-8 border border-slate-100"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
              <input
                type="tel"
                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
              <select
                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
              >
                <option value="">Select a subject</option>
                <option value="general">General inquiry</option>
                <option value="account">Account support</option>
                <option value="loan">Loan information</option>
                <option value="technical">Technical support</option>
              </select>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
              <textarea
                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
              />
            </div>

            <div className="mt-6">
              <span className="block text-sm font-medium text-slate-700 mb-3">Preferred contact</span>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="contact"
                    value="email"
                    checked={formData.preferredContact === 'email'}
                    onChange={(e) => setFormData({ ...formData, preferredContact: e.target.value })}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-slate-700">Email</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="contact"
                    value="phone"
                    checked={formData.preferredContact === 'phone'}
                    onChange={(e) => setFormData({ ...formData, preferredContact: e.target.value })}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-slate-700">Phone</span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="mt-8 w-full inline-flex items-center justify-center px-6 py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
            >
              Send message
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </button>
          </form>
        </div>
      </section>

      {/* Support points */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {supportPoints.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 bg-white rounded-2xl p-6 border border-slate-100"
              >
                <div className="p-2.5 bg-primary-100 rounded-lg shrink-0">
                  <item.icon className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">{item.title}</h3>
                  <p className="text-slate-600 text-sm mt-0.5">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
